/**
 * Zustand slice for the report editor's left panel: active tab/AI view, the unified
 * search box (query/filters/pagination), and search results caching. Consumed by
 * reportEditorPage/leftPanel/ and the hooks in features/products/hooks/.
 */
export const createLeftPanelSearchSlice = (set, get) => ({
    // URL params state (initialized from URL once, then managed by Zustand)
    urlParamsInitialized: false,

    // Left panel tabs state
    leftPanelTab: 'search', // 'search' | 'sourcePackage' | 'aiAssistant' | 'icr'
    aiView: null, // Current AI assistant view
    chatComposerFocusRequestToken: 0,
    chatComposerFocused: false,

    // Search params
    query: '',
    aiSearch: true, // enabled by default
    /** Optional wrapping passage for AI search (e.g. "More Examples"); not URL-synced. */
    searchPassageContext: '',
    showFilters: false,
    keywords: '',
    // Store the last search key to prevent re-fetching when component remounts
    lastSearchKey: null,
    // Flag to indicate if keyword extraction failed in AI search (fallback to vector search)
    keywordGenerationFailed: false,

    // Filter params
    sourceScoreMin: null,
    sourceScoreMax: null,
    docSource: '',
    docSourceType: '',
    /** M6A: document format multi-select (Sub Type) → documentTypes[] on the unified API. */
    subTypes: [],
    tags: [],
    classificationLevels: [],
    // Federated unified search (M6/M6A): which store(s) to search.
    // 'both' (corpus+data_items, legacy) | 'corpus' | 'data_items' | 'reports' | 'all'
    unifiedSourceScope: 'all',
    // M6A unified-search filter dimensions (orthogonal to docSourceType / Sub Type).
    /** Report product types (report arm only), e.g. ['pitch', 'analytic']. */
    productTypes: [],
    /** Ranking mode for unified search: 'keyword' (FTS, default) | 'hybrid' (AI Search). */
    unifiedSearchMode: 'keyword',

    // Section params
    selectedSectionId: null,

    // Search state (derived/computed from search operations)
    currentQuery: '',
    currentAiSearch: true, // enabled by default
    searchResults: [],
    searchIsLoading: false,
    searchKeywords: '',
    // New: track current search id from backend
    searchId: null,
    // New: track top 500 segment IDs from search results (for highlighting in document view)
    searchResultSegmentIds: [],
    // New: cache search summaries by searchId to avoid refetch on remount
    searchSummaries: {},

    // Search pagination state (page -1 means not yet fetched)
    searchPagination: {
        page: -1,
        size: 5,
        totalItems: 0,
        totalPages: 0,
        hasMore: true,
        isLoading: false,
        isInitialized: false,
        lastFetchedPage: -1,
    },

    // Left panel tab setters (with URL sync support via callback)
    setLeftPanelTab: (leftPanelTab, syncToUrl) => {
        set({leftPanelTab});
        if (syncToUrl && typeof syncToUrl === 'function') {
            syncToUrl({leftPanelTab});
        }
    },
    setAiView: (aiView, syncToUrl) => {
        set({aiView});
        if (syncToUrl && typeof syncToUrl === 'function') {
            syncToUrl({aiView});
        }
    },
    requestChatComposerFocus: () => set((state) => ({
        chatComposerFocusRequestToken: state.chatComposerFocusRequestToken + 1,
    })),
    setChatComposerFocused: (chatComposerFocused) => set((state) => (
        state.chatComposerFocused === chatComposerFocused
            ? state
            : {chatComposerFocused}
    )),
    // URL params initialization
    initializeUrlParams: (params) => set((state) => ({
        urlParamsInitialized: true,
        query: params.query ?? '',
        aiSearch: params.aiSearch ?? true,
        showFilters: params.showFilters ?? false,
        keywords: params.keywords ?? '',
        sourceScoreMin: params.sourceScoreMin ?? null,
        sourceScoreMax: params.sourceScoreMax ?? null,
        docSource: params.docSource ?? '',
        docSourceType: params.docSourceType ?? '',
        subTypes: params.subTypes ?? [],
        tags: params.tags ?? [],
        classificationLevels: params.classificationLevels ?? [],
        selectedSectionId: params.selectedSectionId ?? null,
        // Always reset search pagination on initialization so stale page state
        // (e.g. page 1 left over from a previous session) cannot cause an immediate
        // page-1 fetch that returns 0 results and shows "No results found."
        searchResults: [],
        searchResultSegmentIds: [],
        searchId: null,
        lastSearchKey: null,
        searchPagination: {
            ...state.searchPagination,
            page: -1,
            hasMore: true,
            isLoading: false,
            isInitialized: false,
            lastFetchedPage: -1,
        },
    })),

    // Search param setters
    setQuery: (query) => set({query}),
    setAiSearch: (aiSearch) => set({aiSearch}),
    setSearchPassageContext: (searchPassageContext) => set({searchPassageContext}),
    setShowFilters: (showFilters) => set({showFilters}),
    setKeywords: (keywords) => set({keywords}),
    setLastSearchKey: (lastSearchKey) => set({lastSearchKey}),
    setKeywordGenerationFailed: (keywordGenerationFailed) => set({keywordGenerationFailed}),

    // Filter param setters
    setSourceScoreMin: (sourceScoreMin) => set({sourceScoreMin}),
    setSourceScoreMax: (sourceScoreMax) => set({sourceScoreMax}),
    setSourceScoreRange: (min, max) => set({sourceScoreMin: min, sourceScoreMax: max}),
    setDocSource: (docSource) => set({docSource}),
    setDocSourceType: (docSourceType) => set({docSourceType}),
    setSubTypes: (subTypes) => set({subTypes}),
    setTags: (tags) => set({tags}),
    setClassificationLevels: (classificationLevels) => set({classificationLevels}),
    setUnifiedSourceScope: (unifiedSourceScope) => set({unifiedSourceScope}),
    setProductTypes: (productTypes) => set({productTypes}),
    setUnifiedSearchMode: (unifiedSearchMode) => set({unifiedSearchMode}),

    // Section param setters
    setSelectedSectionId: (selectedSectionId) => set({selectedSectionId}),

    // Clear all filters
    clearFilters: () => set({
        sourceScoreMin: null,
        sourceScoreMax: null,
        docSource: '',
        docSourceType: '',
        subTypes: [],
        tags: [],
        classificationLevels: [],
        unifiedSourceScope: 'all',
        productTypes: [],
    }),

    // Search actions
    setCurrentQuery: (currentQuery) => set({currentQuery}),
    setCurrentAiSearch: (currentAiSearch) => set({currentAiSearch}),
    setSearchResults: (searchResults) => set({searchResults}),
    // New: setter for searchId
    setSearchId: (searchId) => set({searchId}),
    // New: setter for searchResultSegmentIds
    setSearchResultSegmentIds: (searchResultSegmentIds) => set({searchResultSegmentIds}),

    // Search pagination actions
    appendSearchResults: (newResults) =>
        set((state) => ({
            searchResults: [...(state.searchResults || []), ...newResults]
        })),

    clearSearchResults: () =>
        set({
            searchResults: [],
            searchResultSegmentIds: [],
            lastSearchKey: null,
            searchId: null,
            searchSummaries: {},
            keywordGenerationFailed: false,
            searchPagination: {
                page: -1,
                size: 5,
                totalItems: 0,
                totalPages: 0,
                hasMore: true,
                isLoading: false,
                isInitialized: false,
                lastFetchedPage: -1,
            },
        }),

    setSearchPagination: (pagination) =>
        set((state) => ({
            searchPagination: {
                ...state.searchPagination,
                ...pagination,
            },
        })),

    resetSearchPagination: () =>
        set({
            searchPagination: {
                page: -1,
                size: 5,
                totalItems: 0,
                totalPages: 0,
                hasMore: true,
                isLoading: false,
                isInitialized: false,
                lastFetchedPage: -1,
            },
        }),

    // Search summary cache setter
    setSearchSummary: (searchId, summary) =>
        set((state) => ({
            searchSummaries: {...state.searchSummaries, [searchId]: summary},
        })),
});
